import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { phoneUtilsService, type PhoneValidationResult } from '../services/phoneUtilsService';

/**
 * React hook for phone number validation using backend utilities
 */
export function usePhoneValidation() {
  return useMutation({
    mutationFn: (phone: string) => phoneUtilsService.validateUSPhone(phone),
    onError: (error) => {

    },
  });
}

/**
 * React hook for phone number formatting using backend utilities
 */
export function usePhoneFormatting() {
  return useMutation({
    mutationFn: (phone: string) => phoneUtilsService.formatPhone(phone),
    onError: (error) => {

    },
  });
}

/**
 * React hook for phone number sanitization using backend utilities
 */
export function usePhoneSanitization() {
  return useMutation({
    mutationFn: (phone: string) => phoneUtilsService.sanitizePhone(phone),
    onError: (error) => {

    },
  });
}

/**
 * React hook for comparing phone numbers using backend utilities
 */
export function usePhoneComparison() {
  return useMutation({
    mutationFn: ({ phone1, phone2 }: { phone1: string; phone2: string }) => 
      phoneUtilsService.comparePhones(phone1, phone2),
    onError: (error) => {

    },
  });
}

/**
 * React hook for batch phone validation using backend utilities
 */
export function useBatchPhoneValidation() {
  const [results, setResults] = useState<PhoneValidationResult[]>([]);
  
  const mutation = useMutation({
    mutationFn: (phones: string[]) => phoneUtilsService.validateBatch(phones),
    onSuccess: (response) => {
      if (response.success && response.data) {
        setResults(response.data.results);
      }
    },
    onError: (error) => {

      setResults([]);
    },
  });

  return {
    validateBatch: mutation.mutate,
    isValidating: mutation.isPending,
    results,
    summary: mutation.data?.success ? mutation.data.data?.summary : null,
    error: mutation.error,
    reset: () => {
      setResults([]);
      mutation.reset();
    }
  };
}

/**
 * React hook for validating default outbound phone numbers
 * Ensures phone is valid and exists in user's assigned Twilio numbers
 */
export function useDefaultOutboundPhoneValidation() {
  return useMutation({
    mutationFn: ({ phone, userPhoneNumbers }: { phone: string; userPhoneNumbers: string[] }) =>
      phoneUtilsService.validateDefaultOutboundPhone(phone, userPhoneNumbers),
    onError: (error) => {

    },
  });
}

/**
 * React hook for getting area code from phone number using backend utilities
 */
export function useAreaCodeExtraction() {
  return useMutation({
    mutationFn: (phone: string) => phoneUtilsService.getAreaCode(phone),
    onError: (error) => {

    },
  });
}

/**
 * Utility hook for common phone operations
 */
export function usePhoneUtils() {
  const validation = usePhoneValidation();
  const formatting = usePhoneFormatting();
  const sanitization = usePhoneSanitization();
  const comparison = usePhoneComparison();
  const areaCode = useAreaCodeExtraction();

  const validatePhone = async (phone: string) => {
    try {
      const result = await validation.mutateAsync(phone);
      return result.success && result.data ? result.data : null;
    } catch (error) {

      return null;
    }
  };

  const formatPhone = async (phone: string) => {
    try {
      const result = await formatting.mutateAsync(phone);
      return result.success && result.data ? result.data.formatted : null;
    } catch (error) {

      return null;
    }
  };

  const sanitizePhone = async (phone: string) => {
    try {
      const result = await sanitization.mutateAsync(phone);
      return result.success && result.data ? result.data.sanitized : null;
    } catch (error) {

      return null;
    }
  };

  const comparePhones = async (phone1: string, phone2: string) => {
    try {
      const result = await comparison.mutateAsync({ phone1, phone2 });
      return result.success && result.data ? result.data.areEqual : false;
    } catch (error) {

      return false;
    }
  };

  const getAreaCode = async (phone: string) => {
    try {
      const result = await areaCode.mutateAsync(phone);
      return result.success && result.data ? result.data.areaCode : null;
    } catch (error) {

      return null;
    }
  };

  return {
    validatePhone,
    formatPhone,
    sanitizePhone,
    comparePhones,
    getAreaCode,
    isLoading: validation.isPending || formatting.isPending || sanitization.isPending || comparison.isPending || areaCode.isPending,
  };
}

export default usePhoneUtils;